import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Paper, Typography, Grid, Box, CircularProgress, Button, makeStyles, ButtonGroup, Tooltip } from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { connect } from 'react-redux';
import 'leaflet/dist/leaflet.css';
import Icon from '@mdi/react';
import {
  mdiSprinkler,
  mdiSprinklerVariant,
  mdiValve,
  mdiValveClosed,
  mdiValveOpen,
  mdiPipeLeak,
  mdiPipeWrench,
  mdiPipeValve,
  mdiPipeDisconnected,
  mdiWaterPump,
  mdiWaterCheck,
  mdiWaterCheckOutline,
  mdiCarPickup,
  mdiClockDigital
} from '@mdi/js';
import { DrawingToolbar, AssetList, AssetEditor, MapProviderControls } from './SupportComponent';
import MapComponent from './AssetMap'

// Define Irrigation Assets Types and Categories
const ASSET_TYPES = {
  SPRINKLER: 'sprinkler',
  VALVE: 'valve',
  CONTROLLER: 'controller',
  PIPE: 'pipe',
  ZONE: 'zone',
  FLUSH_CAP: 'flush_cap',
  MISC: 'misc'
};

// Define Asset Categories with icons
const ASSET_CATEGORIES = {
  [ASSET_TYPES.SPRINKLER]: [
    { id: 'spray_head', name: 'Spray Head', icon: mdiSprinkler, color: '#3498db' },
    { id: 'rotor', name: 'Rotor', icon: mdiSprinklerVariant, color: '#2980b9' },
    { id: 'bubbler', name: 'Bubbler', icon: mdiWaterCheck, color: '#1abc9c' }
  ],
  [ASSET_TYPES.VALVE]: [
    { id: 'gate_valve', name: 'Gate Valve', icon: mdiValve, color: '#e74c3c' },
    { id: 'ball_valve', name: 'Ball Valve', icon: mdiValveOpen, color: '#c0392b' },
    { id: 'zone_valve', name: 'Zone Valve', icon: mdiPipeValve, color: '#d35400' }
  ],
  [ASSET_TYPES.CONTROLLER]: [
    { id: 'timer', name: 'Timer', icon: mdiClockDigital, color: '#9b59b6' },
    { id: 'smart_controller', name: 'Smart Controller', icon: mdiWaterPump, color: '#8e44ad' }
  ],
  [ASSET_TYPES.PIPE]: [
    { id: 'pvc', name: 'PVC Pipe', color: '#bdc3c7' },
    { id: 'poly', name: 'Poly Pipe', color: '#2c3e50' },
    { id: 'mainline', name: 'Mainline', color: '#34495e' }
  ],
  [ASSET_TYPES.ZONE]: [
    { id: 'turf_zone', name: 'Turf Zone', color: '#27ae60' },
    { id: 'drip_zone', name: 'Drip Zone', color: '#16a085' },
    { id: 'garden_zone', name: 'Garden Zone', color: '#2ecc71' }
  ],
  [ASSET_TYPES.FLUSH_CAP]: [
    { id: 'flush_cap', name: 'Flush Cap', icon: mdiPipeDisconnected, color: '#f39c12' }
  ],
  [ASSET_TYPES.MISC]: [
    { id: 'leak', name: 'Leak', icon: mdiPipeLeak, color: '#e74c3c' },
    { id: 'repair', name: 'Repair Site', icon: mdiPipeWrench, color: '#95a5a6' },
    { id: 'truck', name: 'Service Truck', icon: mdiCarPickup, color: '#7f8c8d' }
  ]
};

// Default styles for each asset type
const DEFAULT_STYLES = {
  [ASSET_TYPES.SPRINKLER]: { radius: 3, color: '#3498db', fillColor: '#3498db', fillOpacity: 0.8, weight: 1 },
  [ASSET_TYPES.VALVE]: { radius: 5, color: '#e74c3c', fillColor: '#e74c3c', fillOpacity: 0.8, weight: 1 },
  [ASSET_TYPES.CONTROLLER]: { radius: 8, color: '#9b59b6', fillColor: '#9b59b6', fillOpacity: 0.8, weight: 1 },
  [ASSET_TYPES.PIPE]: { color: '#34495e', weight: 3, opacity: 0.8, lineCap: 'round' },
  [ASSET_TYPES.ZONE]: { color: '#27ae60', fillColor: '#27ae60', fillOpacity: 0.3, weight: 2 },
  [ASSET_TYPES.FLUSH_CAP]: { radius: 4, color: '#f39c12', fillColor: '#f39c12', fillOpacity: 0.8, weight: 1 },
  [ASSET_TYPES.MISC]: { radius: 5, color: '#95a5a6', fillColor: '#95a5a6', fillOpacity: 0.8, weight: 1 }
};

// Providers for the map tiles
const MAP_PROVIDERS = {
  GOOGLE_SATELLITE: {
    name: 'Google Satellite',
    url: 'https://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    maxZoom: 20,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  },
  GOOGLE_HYBRID: {
    name: 'Google Hybrid',
    url: 'https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    maxZoom: 20,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  },
  GOOGLE_MAPS: {
    name: 'Google Maps',
    url: 'https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
    attribution: '&copy; Google Maps',
    maxZoom: 20,
    subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
  }
};

// Define styles
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    margin: theme.spacing(2, 0)
  },
  mapWrapper: {
    position: 'relative',
    width: '100%',
    height: '600px',
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  controlsWrapper: {
    position: 'relative',
    zIndex: 2,
    marginBottom: theme.spacing(2)
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 999
  },
  drawingControls: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius
  },
  assetList: {
    maxHeight: 400,
    overflowY: 'auto',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1)
  },
  assetItem: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover
    },
    '&.selected': {
      backgroundColor: theme.palette.action.selected
    }
  },
  assetTypeGroup: {
    marginBottom: theme.spacing(2),
    '& > *': {
      marginRight: theme.spacing(1),
      marginBottom: theme.spacing(1)
    }
  },
  colorSwatch: {
    width: 24, 
    height: 24, 
    border: '1px solid #ccc', 
    borderRadius: '4px',
    display: 'inline-block', 
    marginLeft: theme.spacing(1), 
    cursor: 'pointer', 
    verticalAlign: 'middle'
  },
  buttonGroup: {
    marginBottom: theme.spacing(2),
    '& > *': {
      margin: theme.spacing(0.5)
    }
  },
  categoryButtonGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2)
  },
  iconButton: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(1),
    minWidth: '80px'
  },
  iconLabel: {
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5),
    textAlign: 'center'
  }
}));

const getMDIIconSvg = (iconPath, color = '#000000', size = 24) => {
  // This creates a proper SVG with the MDI path data
  return `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}">
      <path fill="${color}" d="${iconPath}"/>
    </svg>
  `;
};

const createLeafletIconFromMDI = (iconPath, color, size = 24) => {
  try {
    // Create SVG string
    const svgString = getMDIIconSvg(iconPath, color, size);
    
    // Convert to base64 for compatibility with all browsers
    const base64Svg = btoa(svgString);
    const iconUrl = `data:image/svg+xml;base64,${base64Svg}`;
    
    // Create the Leaflet icon
    return L.icon({
      iconUrl: iconUrl,
      iconSize: [size, size],
      iconAnchor: [size/2, size/2],
      popupAnchor: [0, -size/2]
    });
  } catch (e) {
    console.error("Error creating icon:", e);
    return createFallbackIcon(color);
  }
};
const createFallbackIcon = (color = '#3498db', size = 24) => {
  // Simple colored marker SVG
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="${color}" stroke="#fff" stroke-width="1"/>
    </svg>
  `;
  
  const base64Svg = btoa(svgTemplate);
  const iconUrl = `data:image/svg+xml;base64,${base64Svg}`;
  
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

const getGeometryFromLayer = (layer) => {
  if (!layer) return null;

  try {
    // For markers (point features)
    if (layer.getLatLng && !layer.getRadius) {
      const latlng = layer.getLatLng();
      return { type: 'Point', coordinates: [latlng.lng, latlng.lat] };
    }
    
    // For circles (circle features)
    else if (layer.getLatLng && layer.getRadius) {
      return {
        type: 'Circle',
        coordinates: [layer.getLatLng().lng, layer.getLatLng().lat],
        radius: layer.getRadius()
      };
    }
    
    // For polylines (line features)
    else if (layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
      // This is specifically for polylines, not polygons
      const latlngs = layer.getLatLngs();
      
      // Handle flat array of LatLng objects for polyline
      if (Array.isArray(latlngs) && latlngs.length > 0 && latlngs[0] instanceof L.LatLng) {
        return {
          type: 'LineString',
          coordinates: latlngs.map(latlng => [latlng.lng, latlng.lat])
        };
      }
      
      // Fallback for unexpected structure
      console.warn("Unexpected polyline structure:", latlngs);
      return {
        type: 'LineString',
        coordinates: [[0, 0], [0, 0.001]] // Default to prevent errors
      };
    }
    
    // For polygons (polygon features)
    else if (layer instanceof L.Polygon) {
      try {
        const latlngs = layer.getLatLngs();
        
        // Handle the case of a simple polygon (one ring)
        if (Array.isArray(latlngs) && latlngs[0] instanceof L.LatLng) {
          return {
            type: 'Polygon',
            coordinates: [latlngs.map(latlng => [latlng.lng, latlng.lat])]
          };
        }
        // Handle nested array (multi-polygon)
        else if (Array.isArray(latlngs) && Array.isArray(latlngs[0])) {
          return {
            type: 'Polygon',
            coordinates: [latlngs[0].map(latlng => [latlng.lng, latlng.lat])]
          };
        }
        
        // Fallback
        console.warn("Unexpected polygon structure:", latlngs);
        return {
          type: 'Polygon',
          coordinates: [[[0, 0], [0, 0.001], [0.001, 0.001], [0, 0]]]
        };
      } catch (e) {
        console.error("Error parsing polygon coordinates:", e);
        return {
          type: 'Polygon',
          coordinates: [[[0, 0], [0, 0.001], [0.001, 0.001], [0, 0]]]
        };
      }
    }
  } catch (e) {
    console.error("Error getting geometry from layer:", e);
    return null;
  }
  
  return null;
};

const IrrigationAssetsMap = ({ jobId, jobLocation, user }) => {
  const classes = useStyles();
  
  // Map state
  const [loading, setLoading] = useState(true);
  const [mapInitError, setMapInitError] = useState(null);
  const [mapProvider, setMapProvider] = useState(MAP_PROVIDERS.GOOGLE_HYBRID);
  const [currentZoom, setCurrentZoom] = useState(18); // Higher default zoom for irrigation work
  const [currentCenter, setCurrentCenter] = useState(jobLocation?.coordinates || [33.55, -112.0]);
  
  // Drawing state
  const [activeAssetType, setActiveAssetType] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isDrawingActive, setIsDrawingActive] = useState(false);
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [drawingIntent, setDrawingIntent] = useState({
    assetType: null,
    categoryId: null
  });

  // Map refs
  const mapInstanceRef = useRef(null);
  const drawnItemsLayerRef = useRef(null);
  const drawControlRef = useRef(null);
  const activeAssetTypeRef = useRef(null);
  const activeCategoryRef = useRef(null);
  const drawingIntentRef = useRef({
    assetType: null,
    categoryId: null
  });
  const isProcessingDrawEvent = useRef(false);
  const isInitializedRef = useRef(false)

  useEffect(() => {
    activeAssetTypeRef.current = activeAssetType;
  }, [activeAssetType]);
  
  useEffect(() => {
    activeCategoryRef.current = activeCategory;
  }, [activeCategory]);
  
  // Load map and initialize
  useEffect(() => {
    const initMap = async () => {
      try {
        setLoading(true);
        // In a real application, we would load the saved assets from an API
        if (jobId) {
          try {
            // Placeholder for API call
            // const response = await axios.get(`/api/jobs/${jobId}/irrigation-assets`);
            // setAssets(response.data || []);
            
            // For demo, we'll use local storage
            const savedAssets = localStorage.getItem(`irrigation-assets-${jobId}`);
            if (savedAssets) {
              setAssets(JSON.parse(savedAssets));
            }
          } catch (error) {
            console.error("Failed to load assets:", error);
          }
        }
        
        // Load Leaflet asynchronously if needed
        if (!window.L) {
          const leafletScript = document.createElement('script');
          leafletScript.src = 'https://unpkg.com/leaflet@1.7.1/dist/leaflet.js';
          leafletScript.async = true;
          document.body.appendChild(leafletScript);
          
          await new Promise((resolve) => {
            leafletScript.onload = resolve;
          });
          
          // Load Leaflet Draw
          const leafletDrawScript = document.createElement('script');
          leafletDrawScript.src = 'https://unpkg.com/leaflet-draw@1.0.4/dist/leaflet.draw.js';
          leafletDrawScript.async = true;
          document.body.appendChild(leafletDrawScript);
          
          await new Promise((resolve) => {
            leafletDrawScript.onload = resolve;
          });
        }
        
        setLoading(false);
      } catch (error) {
        console.error("Failed to initialize map:", error);
        setMapInitError("Failed to load map. Please refresh the page.");
        setLoading(false);
      }
    };
    
    initMap();
    
    // Save assets on unmount or changes
    return () => {
      if (jobId && assets.length > 0) {
        localStorage.setItem(`irrigation-assets-${jobId}`, JSON.stringify(assets));
      }
    };
  }, [jobId]);
  
  // Save assets when they change
  useEffect(() => {
    if (jobId && assets.length > 0) {
      localStorage.setItem(`irrigation-assets-${jobId}`, JSON.stringify(assets));
    }
  }, [assets, jobId]);
  
  // Initialize map component when Leaflet is loaded
  useEffect(() => {
    if (isInitializedRef.current || !window.L) return;
    if (loading || mapInitError || !window.L) return;
    
    const mapContainer = document.getElementById('irrigation-map');
    if (!mapContainer) return;
    
    // Create map if not exists
    if (!mapInstanceRef.current) {
      const L = window.L;
      
      // Initialize map
      const map = L.map('irrigation-map', {
        center: currentCenter,
        zoom: currentZoom,
        zoomControl: true
      });
      
      // Add tile layer
      const tileLayer = L.tileLayer(mapProvider.url, {
        attribution: mapProvider.attribution,
        maxZoom: mapProvider.maxZoom,
        subdomains: mapProvider.subdomains
      });
      tileLayer.addTo(map);
      
      // Create feature group for drawn items
      const drawnItems = new L.FeatureGroup();
      map.addLayer(drawnItems);
      
      // Initialize draw control
      const drawControl = new L.Control.Draw({
        edit: {
          featureGroup: drawnItems,
          poly: {
            allowIntersection: false
          }
        },
        draw: {
          polygon: {
            allowIntersection: false,
            showArea: true
          },
          marker: true,
          circle: true,
          circlemarker: false,
          rectangle: false,
          polyline: true
        }
      });
      map.addControl(drawControl);
      
      // Store references
      mapInstanceRef.current = map;
      drawnItemsLayerRef.current = drawnItems;
      drawControlRef.current = drawControl;
      
      // Add existing assets to the map
      renderAssetsOnMap();
      
      // Map events
      map.on('zoomend', () => {
        setCurrentZoom(map.getZoom());
      });
      
      map.on('moveend', () => {
        setCurrentCenter([map.getCenter().lat, map.getCenter().lng]);
      });
      
      map.on('draw:created', (e) => {
        // Only call the handler, don't add the layer here
        if (onDrawCreated) onDrawCreated(e);
      });
      
      map.on('draw:edited', (e) => {
        if (onDrawEdited) onDrawEdited(e);
      });
      
      map.on('draw:deleted', (e) => {
        if (onDrawDeleted) onDrawDeleted(e);
      });

      // Drawing events
      // map.on('draw:created', handleDrawCreated);
      // map.on('draw:edited', handleDrawEdited);
      // map.on('draw:deleted', handleDrawDeleted);

      map.on('draw:drawstart', () => setIsDrawingActive(true));
      map.on('draw:drawstop', () => setIsDrawingActive(false));
      map.on('draw:editstart', () => setIsDrawingActive(true));
      map.on('draw:editstop', () => setIsDrawingActive(false));
      map.on('draw:deletestart', () => setIsDrawingActive(true));
      map.on('draw:deletestop', () => setIsDrawingActive(false));
    }

    mapRef.current = map;
    drawControlRef.current = drawControl;
    drawnItemsRef.current = drawnItems;

    if (onMapReady) {
      onMapReady({
        map,
        drawnItemsLayer: drawnItems,
        drawControlInstance: drawControl
      });
    }
    
    isInitializedRef.current = true;
    
    // Cleanup function
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, []);

    
  //   return () => {
  //     // Clean up map on unmount
  //     if (mapInstanceRef.current) {
  //       mapInstanceRef.current.remove();
  //       mapInstanceRef.current = null;
  //     }
  //   };
  // }, [loading, mapInitError]);
  
  // Render existing assets on the map
  const renderAssetsOnMap = useCallback(() => {
    if (!mapInstanceRef.current || !drawnItemsLayerRef.current) return;
    
    const map = mapInstanceRef.current;
    const drawnItems = drawnItemsLayerRef.current;
    const L = window.L;
    
    // Clear existing items
    drawnItems.clearLayers();
    
    // Add each asset to the map
    assets.forEach(asset => {
      let layer;
      
      // Create layer based on geometry type
      if (asset.geometry.type === 'Point') {
        if (asset.assetType === ASSET_TYPES.SPRINKLER ||
            asset.assetType === ASSET_TYPES.VALVE ||
            asset.assetType === ASSET_TYPES.CONTROLLER ||
            asset.assetType === ASSET_TYPES.FLUSH_CAP ||
            asset.assetType === ASSET_TYPES.MISC) {
          
          // Get the category for this asset
          const category = ASSET_CATEGORIES[asset.assetType]?.find(c => c.id === asset.categoryId);
          
          if (category) {
            try {
              // Use the actual icon path from the category
              const iconPath = category.icon;
              const color = category.color;
              
              // Create proper Leaflet icon if we have an icon path
              let markerIcon;
              if (typeof iconPath === 'string' && iconPath.includes('M')) {
                markerIcon = createLeafletIconFromMDI(iconPath, color);
                console.log(`Created MDI icon marker for: ${asset.name}`);
              } else {
                // Fallback to colored circle if no valid icon path
                markerIcon = createFallbackIcon(color);
                console.log(`Created fallback icon for: ${asset.name} (no valid icon path)`);
              }
              
              // Create the marker with our icon
              layer = L.marker([asset.geometry.coordinates[1], asset.geometry.coordinates[0]], {
                icon: markerIcon,
                title: asset.name
              });
            } catch (e) {
              console.error(`Error creating marker for ${asset.name}:`, e);
              // Fallback to default marker
              layer = L.marker([asset.geometry.coordinates[1], asset.geometry.coordinates[0]]);
            }
          } else {
            // No category found, use default marker
            layer = L.marker([asset.geometry.coordinates[1], asset.geometry.coordinates[0]]);
          }
          
          // Add popup with asset information
          layer.bindPopup(`
            <strong>${asset.name}</strong><br>
            Type: ${asset.assetType}<br>
            Category: ${category?.name || 'None'}<br>
            ${asset.notes ? `Notes: ${asset.notes}` : ''}
          `);
        }
      } else if (asset.geometry.type === 'Circle') {
        const center = [asset.geometry.coordinates[1], asset.geometry.coordinates[0]];
        const radius = asset.geometry.radius;
        layer = L.circle(center, {
          radius,
          ...asset.style
        });
      
      } else if (asset.geometry.type === 'LineString') {
        // For pipes (polylines)
        try {
          // Add safety checks
          if (Array.isArray(asset.geometry.coordinates) && asset.geometry.coordinates.length >= 2) {
            const coordinates = asset.geometry.coordinates.map(coord => [coord[1], coord[0]]);
            layer = L.polyline(coordinates, {
              ...asset.style,
              // Ensure pipe-specific styles
              color: asset.style?.color || DEFAULT_STYLES[ASSET_TYPES.PIPE].color,
              weight: asset.style?.weight || DEFAULT_STYLES[ASSET_TYPES.PIPE].weight,
              opacity: asset.style?.opacity || DEFAULT_STYLES[ASSET_TYPES.PIPE].opacity,
              lineCap: 'round',
              dashArray: asset.style?.dashArray
            });
            console.log("Created polyline for pipe:", asset.name);
          } else {
            console.warn("Invalid LineString coordinates for asset:", asset.id);
            // Create a minimal valid polyline to avoid errors
            layer = L.polyline([[0, 0], [0, 0.001]], DEFAULT_STYLES[ASSET_TYPES.PIPE]);
          }
        } catch (e) {
          console.error("Error creating polyline:", e);
          layer = L.polyline([[0, 0], [0, 0.001]], DEFAULT_STYLES[ASSET_TYPES.PIPE]);
        }
      } else if (asset.geometry.type === 'Polygon') {
        // Add safety checks
        if (Array.isArray(asset.geometry.coordinates) && 
            asset.geometry.coordinates.length > 0 && 
            Array.isArray(asset.geometry.coordinates[0]) &&
            asset.geometry.coordinates[0].length >= 3) { // Polygon needs at least 3 points
          const coordinates = asset.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
          layer = L.polygon(coordinates, asset.style);
        } else {
          console.warn("Invalid Polygon coordinates for asset:", asset.id);
          // Create a minimal valid polygon to avoid errors
          layer = L.polygon([[0, 0], [0, 0.001], [0.001, 0.001]], asset.style);
        }
      }
      if (layer) {
        // Add asset ID to the layer for later reference
        layer._assetId = asset.id;
        
        // Add popup with asset information
        layer.bindPopup(`
          <strong>${asset.name}</strong><br>
          Type: ${asset.assetType}<br>
          Category: ${asset.categoryId}<br>
          ${asset.notes ? `Notes: ${asset.notes}` : ''}
        `);
        
        // Add layer to the map
        drawnItems.addLayer(layer);
      }
    });
  }, [assets]);
  
  // Update map when assets change
  useEffect(() => {
    renderAssetsOnMap();
  }, [assets, renderAssetsOnMap]);
  
  useEffect(() => {
    console.log("Assets state updated:", assets);
  }, [assets]);

  const handleDrawCreated = useCallback((event) => {
    // Prevent duplicate event processing
    if (isProcessingDrawEvent.current) {
      console.log("Ignoring duplicate draw event");
      return;
    }
    
    // Set the flag to indicate we're processing an event
    isProcessingDrawEvent.current = true;
    
    console.log("Draw created event:", event);
    console.log("Drawing intent from ref:", drawingIntentRef.current);
    console.log("Layer type:", event.layerType);
    
    // Use the ref instead of state
    if (!drawingIntentRef.current.assetType || !drawingIntentRef.current.categoryId) {
      alert("Please select an asset type and category before drawing");
      // Remove the layer that was just created
      if (event.layer && drawnItemsLayerRef.current) {
        drawnItemsLayerRef.current.removeLayer(event.layer);
      }
      // Reset the processing flag
      isProcessingDrawEvent.current = false;
      return;
    }
    
    const layer = event.layer;
    const layerType = event.layerType;
    
    // Fix getGeometryFromLayer and handle the geometry
    let geometry = getGeometryFromLayer(layer);
    
    // Override the geometry type for polylines
    if (layerType === 'polyline' && drawingIntentRef.current.assetType === ASSET_TYPES.PIPE) {
      // Make sure this is recognized as a LineString
      if (geometry && geometry.type !== 'LineString') {
        geometry.type = 'LineString';
        console.log("Corrected geometry type to LineString for pipe");
      }
    }
    
    if (!geometry) {
      console.error("Failed to extract geometry from created layer");
      // Reset the processing flag
      isProcessingDrawEvent.current = false;
      return;
    }
    
    console.log("Extracted geometry:", geometry);
    
    // Create new asset using the ref values
    const assetType = drawingIntentRef.current.assetType;
    const categoryId = drawingIntentRef.current.categoryId;
    const category = ASSET_CATEGORIES[assetType]?.find(c => c.id === categoryId);
    
    // Get count of this type/category for proper naming
    const existingCount = assets.filter(
      a => a.assetType === assetType && a.categoryId === categoryId
    ).length;
    
    
    const newAsset = {
      id: uuidv4(),
      name: `${category?.name || 'Unnamed'} ${existingCount + 1}`,
      assetType: assetType,
      categoryId: categoryId,
      geometry: geometry,
      style: { ...DEFAULT_STYLES[assetType] },
      notes: '',
      createdAt: new Date().toISOString(),
      createdBy: user?.id || 'unknown'
    };
    
    console.log("New asset to add:", newAsset);
    
    // Add to assets
    setAssets(prev => [...prev, newAsset]);
    
    // Add layer ID for reference
    layer._assetId = newAsset.id;
    
    // Set selection and editing
    setSelectedAssetId(newAsset.id);
    setEditingAsset({ ...newAsset });
    
    // Reset drawing mode
    setIsDrawingActive(false);
    
    // Use setTimeout to reset the flag after a short delay
    // This prevents any potential race conditions with event handling
    setTimeout(() => {
      isProcessingDrawEvent.current = false;
    }, 100);
  }, [assets, user]);

  // Handle when a shape is edited
  const handleDrawEdited = useCallback((event) => {
    event.layers.eachLayer(layer => {
      const assetId = layer._assetId;
      if (assetId) {
        const newGeometry = getGeometryFromLayer(layer);
        if (!newGeometry) return;
        
        setAssets(prev => prev.map(asset => 
          asset.id === assetId ? { ...asset, geometry: newGeometry } : asset
        ));
        
        if (selectedAssetId === assetId && editingAsset?.id === assetId) {
          setEditingAsset(prev => ({ ...prev, geometry: newGeometry }));
        }
      }
    });
  }, [selectedAssetId, editingAsset]);
  
  // Handle when a shape is deleted
  const handleDrawDeleted = useCallback((event) => {
    const deletedIds = [];
    event.layers.eachLayer(layer => {
      if (layer._assetId) deletedIds.push(layer._assetId);
    });
    
    if (deletedIds.length > 0) {
      console.log("Deleting assets:", deletedIds);
      setAssets(prev => prev.filter(asset => !deletedIds.includes(asset.id)));
      
      if (selectedAssetId && deletedIds.includes(selectedAssetId)) {
        setSelectedAssetId(null);
        setEditingAsset(null);
      }
    }
  }, [selectedAssetId]);

  useEffect(() => {
    // Update drawingIntent when active selections change
    if (activeAssetType && activeCategory) {
      setDrawingIntent({
        assetType: activeAssetType,
        categoryId: activeCategory
      });
      console.log("Updated drawing intent:", { assetType: activeAssetType, categoryId: activeCategory });
    }
  }, [activeAssetType, activeCategory]);

  // Handle tool activation
  const activateDrawingTool = useCallback((toolType) => {
    console.log("Activating tool:", toolType);
    
    // Ensure both type and category are selected and stored in ref
    if (!activeAssetType || !activeCategory) {
      alert("Please select an asset type and category before drawing");
      return;
    }
    
    // Make sure the ref is updated with the latest values
    drawingIntentRef.current = {
      assetType: activeAssetType,
      categoryId: activeCategory
    };
    
    console.log("Drawing intent ref before activation:", drawingIntentRef.current);
    
    if (isDrawingActive) {
      console.log("Drawing is already active, can't start a new drawing session");
      return;
    }
    
    const map = mapInstanceRef.current;
    const drawControl = drawControlRef.current;
    
    if (!map || !drawControl) {
      console.error("Map or draw control not initialized");
      return;
    }
    
    // Reset selection
    setSelectedAssetId(null);
    setEditingAsset(null);
    
    // Activate drawing tool
    console.log(`Activating drawing tool: ${toolType}`);
    let handler;
    
    try {
      const drawOptions = drawControl.options?.draw;
      
      switch(toolType) {
        case 'polygon':
          handler = new L.Draw.Polygon(map, drawOptions?.polygon);
          break;
        case 'marker':
          // Customize the marker icon for drawing 
          if (activeAssetType && activeCategory) {
            const category = ASSET_CATEGORIES[activeAssetType]?.find(c => c.id === activeCategory);
            if (category?.icon) {
              try {
                // Create a custom icon for the draw marker
                const iconPath = category.icon;
                const color = category.color;
                const drawIcon = createLeafletIconFromMDI(iconPath, color);
                
                // Apply custom icon to the draw handler
                const markerOptions = {
                  icon: drawIcon,
                  ...drawOptions?.marker
                };
                handler = new L.Draw.Marker(map, markerOptions);
                console.log("Created custom draw marker with icon");
              } catch (e) {
                console.error("Error creating custom draw marker icon:", e);
                handler = new L.Draw.Marker(map, drawOptions?.marker);
              }
            } else {
              handler = new L.Draw.Marker(map, drawOptions?.marker);
            }
          } else {
            handler = new L.Draw.Marker(map, drawOptions?.marker);
          }
          break;
        // case 'marker':
        //   handler = new L.Draw.Marker(map, drawOptions?.marker);
        //   break;
        case 'circle':
          handler = new L.Draw.Circle(map, drawOptions?.circle);
          break;
        case 'polyline':
          // Make sure we're using the polyline tool with appropriate options
          handler = new L.Draw.Polyline(map, {
            shapeOptions: {
              color: DEFAULT_STYLES[ASSET_TYPES.PIPE].color,
              weight: DEFAULT_STYLES[ASSET_TYPES.PIPE].weight,
              opacity: DEFAULT_STYLES[ASSET_TYPES.PIPE].opacity
            },
            showLength: true,
            metric: false, // Use feet instead of meters
            feet: true,
            ...drawOptions?.polyline
          });
          console.log("Activated polyline drawing tool for pipes");
          break;
        default:
          console.warn("Unknown drawing tool type:", toolType);
          return;
      }
      
      if (handler) {
        handler.enable();
        setIsDrawingActive(true);
      }
    } catch (e) {
      console.error(`Error enabling drawing tool ${toolType}:`, e);
      setIsDrawingActive(false);
    }
  }, [isDrawingActive, activeAssetType, activeCategory]);
  
  // Handle asset selection
  const handleSelectAsset = useCallback((asset) => {
    if (isDrawingActive) return;
    
    setSelectedAssetId(asset.id);
    setEditingAsset({ ...asset });
    
    // Zoom to asset
    const layer = drawnItemsLayerRef.current?.getLayers().find(l => l._assetId === asset.id);
    if (layer && mapInstanceRef.current) {
      try {
        if (layer.getBounds) {
          mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 20 });
        } else if (layer.getLatLng) {
          mapInstanceRef.current.setView(layer.getLatLng(), Math.max(mapInstanceRef.current.getZoom(), 19));
        }
      } catch (e) {
        console.error("Error zooming to selected asset:", e);
      }
    }
  }, [isDrawingActive]);
  
  // Handle asset deletion
  const handleDeleteAsset = useCallback((idToDelete) => {
    if (isDrawingActive) return;
    
    const assetToDelete = assets.find(a => a.id === idToDelete);
    if (!assetToDelete || !window.confirm(`Delete "${assetToDelete.name}"?`)) return;
    
    // Remove layer from map
    const layer = drawnItemsLayerRef.current?.getLayers().find(l => l._assetId === idToDelete);
    if (layer) drawnItemsLayerRef.current.removeLayer(layer);
    
    // Remove from state
    setAssets(prev => prev.filter(a => a.id !== idToDelete));
    
    if (selectedAssetId === idToDelete) {
      setSelectedAssetId(null);
      setEditingAsset(null);
    }
  }, [isDrawingActive, assets, selectedAssetId]);
  
  // Clear all assets
  const handleClearAllAssets = useCallback(() => {
    if (isDrawingActive || !window.confirm('Delete ALL saved assets? This cannot be undone.')) return;
    
    setAssets([]);
    drawnItemsLayerRef.current?.clearLayers();
    setSelectedAssetId(null);
    setEditingAsset(null);
    localStorage.removeItem(`irrigation-assets-${jobId}`);
  }, [isDrawingActive, jobId]);
  
  // Handle editor changes
  const handleAssetEditorChange = useCallback((event) => {
    const { name, value } = event.target;
    setEditingAsset(prev => prev ? { ...prev, [name]: value } : null);
  }, []);
  
  // Handle style changes
  const handleAssetStyleChange = useCallback((property, value) => {
    setEditingAsset(prev => {
      if (!prev) return null;
      
      const assetType = prev.assetType;
      const defaultForType = DEFAULT_STYLES[assetType] || {};
      const updatedStyle = { ...(prev.style || defaultForType), [property]: value };
      
      return { ...prev, style: updatedStyle };
    });
  }, []);
  
  // Save edited asset
  const handleAssetEditorSave = useCallback(() => {
    if (!editingAsset || !selectedAssetId) return;
    
    setAssets(prev => prev.map(a => a.id === selectedAssetId ? { ...editingAsset } : a));
    
    // Update layer style
    const layer = drawnItemsLayerRef.current?.getLayers().find(l => l._assetId === selectedAssetId);
    if (layer && layer.setStyle && editingAsset.style) {
      try {
        layer.setStyle(editingAsset.style);
      } catch (e) {
        console.error("Error applying style on save:", e);
      }
    }
    
    setSelectedAssetId(null);
    setEditingAsset(null);
  }, [editingAsset, selectedAssetId]);
  
  // Cancel editing
  const handleAssetEditorCancel = useCallback(() => {
    setSelectedAssetId(null);
    setEditingAsset(null);
  }, []);
  
  // Change map provider
  const handleProviderChange = useCallback((provider) => {
    if (!mapInstanceRef.current) return;
    
    const map = mapInstanceRef.current;
    map.eachLayer(layer => {
      if (layer instanceof L.TileLayer) {
        map.removeLayer(layer);
      }
    });
    
    const newTileLayer = L.tileLayer(provider.url, {
      attribution: provider.attribution,
      maxZoom: provider.maxZoom,
      subdomains: provider.subdomains
    });
    
    newTileLayer.addTo(map);
    setMapProvider(provider);
  }, []);
  
  // Select asset type and category
  const handleAssetTypeSelect = useCallback((type) => {
    console.log("Setting asset type to:", type);
    setActiveAssetType(type);
    setActiveCategory(null); // Reset category when type changes
    
    // Store in ref
    drawingIntentRef.current = {
      assetType: type,
      categoryId: null
    };
    
    // Reset any active drawing mode
    setIsDrawingActive(false);
  }, []);

  const handleCategorySelect = useCallback((categoryId) => {
    console.log("Setting category to:", categoryId);
    setActiveCategory(categoryId);
    
    // Update ref with current selection
    drawingIntentRef.current = {
      assetType: activeAssetType,
      categoryId: categoryId
    };
    
    console.log("Updated drawing intent ref:", drawingIntentRef.current);
  }, [activeAssetType]);
  
  
  // Render loading state
  if (loading) {
    return (
      <Paper className={classes.root} elevation={3}>
        <Typography variant="h5" gutterBottom>Irrigation Assets Map</Typography>
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <CircularProgress />
          <Typography variant="body1" style={{ marginLeft: 16 }}>
            Loading map components...
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  // Render error state
  if (mapInitError) {
    return (
      <Paper className={classes.root} elevation={3}>
        <Typography variant="h5" gutterBottom>Irrigation Assets Map</Typography>
        <Box display="flex" justifyContent="center" alignItems="center" height={400}>
          <Typography variant="body1" color="error">
            {mapInitError}
          </Typography>
        </Box>
      </Paper>
    );
  }
  
  return (
    <Paper className={classes.root} elevation={3}>
      <Typography variant="h5" gutterBottom>Irrigation Assets Map</Typography>
      
      {/* Map Provider Controls */}
      <Box className={classes.controlsWrapper}>
        <MapProviderControls
          providers={Object.values(MAP_PROVIDERS)}
          currentProvider={mapProvider}
          onProviderChange={handleProviderChange}
          isDrawingMode={isDrawingActive}
        />
      </Box>
      
      <Box className={classes.mapWrapper}>
      <MapErrorBoundary>
        <MapComponent
          provider={mapProvider}
          drawings={assets}
          initialCenter={currentCenter}
          initialZoom={currentZoom}
          onMapReady={({ map, drawnItemsLayer, drawControlInstance }) => {
            mapInstanceRef.current = map;
            drawnItemsLayerRef.current = drawnItemsLayer;
            drawControlRef.current = drawControlInstance;
            
            // Set up event handlers
            map.on('draw:created', handleDrawCreated);
            map.on('draw:edited', handleDrawEdited);
            map.on('draw:deleted', handleDrawDeleted);
            map.on('draw:drawstart', () => setIsDrawingActive(true));
            map.on('draw:drawstop', () => setIsDrawingActive(false));
            map.on('draw:editstart', () => setIsDrawingActive(true));
            map.on('draw:editstop', () => setIsDrawingActive(false));
            map.on('draw:deletestart', () => setIsDrawingActive(true));
            map.on('draw:deletestop', () => setIsDrawingActive(false));
            
            // Set up map event handlers
            map.on('zoomend', () => {
              setCurrentZoom(map.getZoom());
            });
            
            map.on('moveend', () => {
              setCurrentCenter([map.getCenter().lat, map.getCenter().lng]);
            });
          }}
          onMapViewChange={({ center, zoom }) => {
            setCurrentCenter([center.lat, center.lng]);
            setCurrentZoom(zoom);
          }}
          onDrawCreated={handleDrawCreated}
          onDrawEdited={handleDrawEdited}
          onDrawDeleted={handleDrawDeleted}
          drawingMode={activeAssetType}
          isDrawingToolActive={isDrawingActive}
        />
        </MapErrorBoundary>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
          </div>
        )}
      </Box>
      
      <Box mt={1} display="flex" justifyContent="space-between" flexWrap="wrap">
        <Typography variant="caption" color="textSecondary">
          Zoom: {currentZoom?.toFixed(1)}
        </Typography>
        <Typography variant="caption" color="textSecondary" dangerouslySetInnerHTML={{ __html: mapProvider.attribution }} />
      </Box>
      
      {/* Drawing Controls Section */}
      <Box className={classes.drawingControls}>
        <Typography variant="h6" gutterBottom>Irrigation Assets</Typography>
        
        {/* Asset Type Selection */}
        <Box className={classes.assetTypeGroup}>
          <Typography variant="body2" gutterBottom>1. Select Asset Type:</Typography>
          <ButtonGroup size="small" variant="outlined" className={classes.buttonGroup}>
            {Object.entries(ASSET_TYPES).map(([key, value]) => (
              <Button
                key={key}
                onClick={() => handleAssetTypeSelect(value)}
                variant={activeAssetType === value ? 'contained' : 'outlined'}
                color={activeAssetType === value ? 'primary' : 'default'}
                disabled={isDrawingActive}
              >
                {value.replace('_', ' ').toUpperCase()}
              </Button>
            ))}
          </ButtonGroup>
        </Box>
        
        {/* Asset Category Selection */}
        {activeAssetType && (
          <Box className={classes.assetTypeGroup}>
            <Typography variant="body2" gutterBottom>2. Select Category:</Typography>
            <div className={classes.categoryButtonGroup}>
              {ASSET_CATEGORIES[activeAssetType]?.map(category => (
                <Tooltip key={category.id} title={category.name}>
                  <Button
                    className={classes.iconButton}
                    onClick={() => handleCategorySelect(category.id)}
                    variant={activeCategory === category.id ? 'contained' : 'outlined'}
                    color={activeCategory === category.id ? 'primary' : 'default'}
                    disabled={isDrawingActive}
                    style={{ borderColor: category.color }}
                  >
                    {category.icon ? (
                      <Icon path={category.icon} size={1} color={category.color} />
                    ) : (
                      <div className={classes.colorSwatch} style={{ backgroundColor: category.color }} />
                    )}
                    <span className={classes.iconLabel}>{category.name}</span>
                  </Button>
                </Tooltip>
              ))}
            </div>
          </Box>
        )}
        

        {activeAssetType && activeCategory && (
          <DrawingToolbar
            onToolActivate={activateDrawingTool}
            isDrawingMode={isDrawingActive}
            activeAssetType={activeAssetType}
            activeCategory={activeCategory}
          />
        )}
        
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>Asset List</Typography>
          <AssetList
            assets={assets}
            selectedAssetId={selectedAssetId}
            onSelectAsset={handleSelectAsset}
            onDeleteAsset={handleDeleteAsset}
            onClearAll={handleClearAllAssets}
            isDrawingMode={isDrawingActive}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle1" gutterBottom>Asset Details</Typography>
          <AssetEditor
            editingAsset={editingAsset}
            onEditChange={handleAssetEditorChange}
            onStyleChange={handleAssetStyleChange}
            onSave={handleAssetEditorSave}
            onCancel={handleAssetEditorCancel}
            isDrawingMode={isDrawingActive}
          />
        </Grid>
      </Box>
      <Box mt={2} p={2} border="1px solid #eee" borderRadius={1}>
        <Typography variant="caption">
          Debug: Drawing Intent: {JSON.stringify(drawingIntentRef.current)}
        </Typography>
      </Box>
    </Paper>
  );
};

// Import TextField for Editor
import { TextField } from '@material-ui/core';

// Connect to Redux if needed
export default IrrigationAssetsMap
// connect(
//   (state) => ({
//     // jobId: state.job?.currentJob?.id || null,
//     // jobLocation: state.job?.currentJob?.location || null,
//     user: state.auth?.user || null
//   })
// )(IrrigationAssetsMap);


class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Map error caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Paper elevation={3} style={{ padding: 16, margin: '16px 0' }}>
          <Typography variant="h6" color="error">
            Map Error
          </Typography>
          <Typography variant="body2">
            There was a problem rendering the map. Please try refreshing the page.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            style={{ marginTop: 16 }}
            onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Paper>
      );
    }

    return this.props.children;
  }
}

const createMarkerIcon = (color = '#3498db', size = 30) => {
  // Simple colored circle SVG icon
  const svgTemplate = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" fill="${color}" stroke="#fff" stroke-width="1"/>
    </svg>
  `;
  
  const iconUrl = `data:image/svg+xml;base64,${btoa(svgTemplate)}`;
  
  return L.icon({
    iconUrl: iconUrl,
    iconSize: [size, size],
    iconAnchor: [size/2, size/2],
    popupAnchor: [0, -size/2]
  });
};

